import { createContext, useEffect, useState } from "react";
import {
  getAccessToken,
  getRole,
  hasSession,
  restoreSession,
  SESSION_CHANGED_EVENT,
} from "./auth/authSession";
import { asPermissionList } from "./utils/permissions";

const apiUrl = process.env.REACT_APP_API_URL;
export const PermissionContext = createContext();

const sameJson = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [departmentAccess, setDepartmentAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRolePermissions = async ({ silent = false } = {}) => {
      if (!hasSession()) {
        setPermissions([]);
        setDepartmentAccess(null);
        setLoading(false);
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      if (!getAccessToken()) {
        await restoreSession();
      }
      if (!hasSession()) {
        setPermissions([]);
        setDepartmentAccess(null);
        setLoading(false);
        return;
      }

      const role = getRole();
      if (!role) {
        setPermissions([]);
        setDepartmentAccess(null);
        setLoading(false);
        return;
      }

      try {
        const encodedRole = encodeURIComponent(role);
        const [permResponse, accessResponse] = await Promise.all([
          fetch(`${apiUrl}/api/role-permissions/role/${encodedRole}`, {
            method: "GET",
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }),
          fetch(
            `${apiUrl}/api/role-department-permissions/role/${encodedRole}`,
            {
              method: "GET",
              headers: {
                "Cache-Control": "no-cache",
                Pragma: "no-cache",
              },
            }
          ),
        ]);

        if (!permResponse.ok) {
          throw new Error(`HTTP error! status: ${permResponse.status}`);
        }

        const data = await permResponse.json();
        const nextPermissions = asPermissionList(data);
        setPermissions((prev) =>
          sameJson(prev, nextPermissions) ? prev : nextPermissions
        );
        if (accessResponse.ok) {
          const access = await accessResponse.json();
          setDepartmentAccess((prev) => (sameJson(prev, access) ? prev : access));
        } else if (!silent) {
          setDepartmentAccess(null);
        }
        setLoading(false);
      } catch (err) {
        if (!silent) {
          setPermissions([]);
          setDepartmentAccess(null);
        }
        setLoading(false);
        console.error("Failed to fetch role permissions:", err);
      }
    };

    fetchRolePermissions();
    const interval = setInterval(
      () => fetchRolePermissions({ silent: true }),
      30000
    );
    const onSessionChanged = () => fetchRolePermissions();
    window.addEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener(SESSION_CHANGED_EVENT, onSessionChanged);
    };
  }, []);

  return (
    <PermissionContext.Provider value={{ permissions, departmentAccess, loading }}>
      {children}
    </PermissionContext.Provider>
  );
};
